import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import type { PluginConfiguration } from '../malleableUiPluginType';
import { parseMauiDiagramLink, type RovoConversationContext } from '../pm-plugins/detect-maui-link';
import {
	executeMauiEmbedFinalize,
	getMauiDiagramPasteView,
	MauiEmbedFinalizeStatus,
	mauiDiagramPastePluginKey,
	type MauiEmbedFinalizeMode,
	type MauiEmbedFinalizeWorkflowArgs,
	type MauiEmbedTargetSpace,
} from '../pm-plugins/maui-diagram-paste-plugin';

export type FinalizeMauiEmbedFromIndicatorArgs = {
	contentId?: string;
	conversationContext?: RovoConversationContext;
	mode?: MauiEmbedFinalizeMode;
	originalUrl: string;
	/** When supplied, skips the probe and finalizes into the picked space. */
	targetSpace?: MauiEmbedTargetSpace;
};

export type FinalizeMauiEmbedFromIndicator = (
	args: FinalizeMauiEmbedFromIndicatorArgs,
) => Promise<
	{ contentId: string; status: 'finalized' } | { contentId?: string; status: 'not-finalized' }
>;

export function makeFinalizeMauiEmbedFromIndicator({
	config,
}: {
	config: PluginConfiguration | undefined;
}): FinalizeMauiEmbedFromIndicator {
	return async (args: FinalizeMauiEmbedFromIndicatorArgs) => {
		if (
			args.targetSpace &&
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
			FeatureGates.getExperimentValue<boolean>(
				'maui-native-embed-finalize-diagram-anywhere',
				'value',
				false,
			)
		) {
			return { status: 'not-finalized' };
		}

		const diagramPaste = config?.diagramPaste;
		if (!diagramPaste) {
			return { status: 'not-finalized' };
		}

		const { originalUrl, conversationContext, mode, targetSpace } = args;

		// Use the view that owns this embed URL.
		const view = getMauiDiagramPasteView(originalUrl);
		if (!view) {
			return { status: 'not-finalized' };
		}
		let contentId = args.contentId;
		if (!contentId) {
			const parsed = parseMauiDiagramLink(originalUrl);
			contentId = parsed?.contentId;
		}
		if (!contentId) {
			return { status: 'not-finalized' };
		}

		const workflowArgs: MauiEmbedFinalizeWorkflowArgs = {
			contentId,
			originalUrl,
			conversationContext,
			mode,
			targetSpace,
		};

		await executeMauiEmbedFinalize(view, diagramPaste, workflowArgs);
		const status = mauiDiagramPastePluginKey.getState(view.state)?.embedFinalizeStates[contentId]
			?.status;
		return status === MauiEmbedFinalizeStatus.FINALIZED ||
			status === MauiEmbedFinalizeStatus.NOT_NEEDED
			? { status: 'finalized', contentId }
			: { status: 'not-finalized', contentId };
	};
}
